import { useCallback, useEffect, useState } from 'react';
import {
  Linking as RNLinking,
  Platform,
  Text,
  View,
} from 'react-native';
import * as ExpoLinking from 'expo-linking';
import { useIsFocused, useLocalSearchParams } from 'expo-router';
import { trpc } from '../lib/trpc';
import { maybeCompleteOAuthSession, openOAuthSessionAsync } from '../lib/oauth-session';
import { Button } from './Button';
import { Card } from './Card';
import { SectionHeader } from './SectionHeader';

maybeCompleteOAuthSession();

const GOOGLE_SHEETS_NATIVE_REDIRECT_URI = 'perdiem://google-sheets-callback';

export function GoogleSheetsIntegrationCard({ isDemo = false }: { isDemo?: boolean }) {
  const isFocused = useIsFocused();
  const params = useLocalSearchParams<{ googleSheets?: string; message?: string }>();
  const utils = trpc.useUtils();
  const googleSheetsQ = trpc.googleSheets.getStatus.useQuery(undefined, { enabled: isFocused });
  const googleSheetsStartMutation = trpc.googleSheets.startConnect.useMutation();
  const googleSheetsSyncMutation = trpc.googleSheets.syncNow.useMutation({
    onSuccess: () => {
      utils.googleSheets.getStatus.invalidate();
    },
  });
  const googleSheetsDisconnectMutation = trpc.googleSheets.disconnect.useMutation({
    onSuccess: () => {
      utils.googleSheets.getStatus.invalidate();
    },
  });

  const [googleSheetsMessage, setGoogleSheetsMessage] = useState<string | null>(null);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(null);

  useEffect(() => {
    if (params.googleSheets === 'connected') {
      setGoogleSheetsMessage('Google Sheets connected. Initial sync has been queued.');
      setGoogleSheetsError(null);
      void utils.googleSheets.getStatus.invalidate();
    } else if (params.googleSheets === 'error') {
      setGoogleSheetsMessage(null);
      setGoogleSheetsError(params.message ?? 'Google Sheets connection failed.');
    }
  }, [params.googleSheets, params.message, utils.googleSheets.getStatus]);

  const handleConnectGoogleSheets = useCallback(async () => {
    setGoogleSheetsMessage(null);
    setGoogleSheetsError(null);

    const returnTo =
      Platform.OS === 'web'
        ? `${globalThis.location?.origin ?? process.env['EXPO_PUBLIC_SITE_URL'] ?? 'http://localhost:8081'}/integrations`
        : GOOGLE_SHEETS_NATIVE_REDIRECT_URI;

    try {
      const { authUrl } = await googleSheetsStartMutation.mutateAsync({ returnTo });

      if (Platform.OS === 'web') {
        await RNLinking.openURL(authUrl);
        return;
      }

      const result = await openOAuthSessionAsync(authUrl, GOOGLE_SHEETS_NATIVE_REDIRECT_URI);
      if (result.type !== 'success' || !result.url) {
        setGoogleSheetsError('Google Sheets connection was canceled.');
        return;
      }

      const { queryParams } = ExpoLinking.parse(result.url);
      const status = typeof queryParams?.googleSheets === 'string' ? queryParams.googleSheets : null;
      const message = typeof queryParams?.message === 'string' ? queryParams.message : null;

      if (status !== 'connected') {
        setGoogleSheetsError(message ?? 'Google Sheets connection failed.');
        return;
      }

      setGoogleSheetsMessage('Google Sheets connected. Initial sync has been queued.');
      await utils.googleSheets.getStatus.invalidate();
    } catch (e) {
      setGoogleSheetsError(e instanceof Error ? e.message : 'Could not start Google Sheets connection.');
    }
  }, [googleSheetsStartMutation, utils.googleSheets.getStatus]);

  const handleSyncGoogleSheetsNow = useCallback(async () => {
    setGoogleSheetsMessage(null);
    setGoogleSheetsError(null);
    try {
      await googleSheetsSyncMutation.mutateAsync();
      setGoogleSheetsMessage('Google Sheets sync queued.');
    } catch (e) {
      setGoogleSheetsError(e instanceof Error ? e.message : 'Could not queue Google Sheets sync.');
    }
  }, [googleSheetsSyncMutation]);

  const handleDisconnectGoogleSheets = useCallback(async () => {
    setGoogleSheetsMessage(null);
    setGoogleSheetsError(null);
    try {
      await googleSheetsDisconnectMutation.mutateAsync();
      setGoogleSheetsMessage('Google Sheets disconnected.');
    } catch (e) {
      setGoogleSheetsError(e instanceof Error ? e.message : 'Could not disconnect Google Sheets.');
    }
  }, [googleSheetsDisconnectMutation]);

  const handleOpenGoogleSheet = useCallback(() => {
    if (!googleSheetsQ.data?.spreadsheetUrl) return;
    RNLinking.openURL(googleSheetsQ.data.spreadsheetUrl).catch(() => undefined);
  }, [googleSheetsQ.data?.spreadsheetUrl]);

  return (
    <Card>
      <SectionHeader title="Google Sheets" />
      <Text className="mb-3 text-sm leading-5 text-gray-600 dark:text-gray-300">
        Export your Per Diem data to a managed spreadsheet that stays updated automatically.
      </Text>
      <View className="flex-row items-center justify-between py-2">
        <Text className="text-sm text-gray-600 dark:text-gray-400">Status</Text>
        <Text className="text-sm text-gray-800 capitalize dark:text-gray-200">
          {googleSheetsQ.data?.status ?? 'disconnected'}
        </Text>
      </View>
      <View className="flex-row items-center justify-between py-2">
        <Text className="text-sm text-gray-600 dark:text-gray-400">Last synced</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400">
          {googleSheetsQ.data?.lastSyncedAt
            ? new Date(googleSheetsQ.data.lastSyncedAt).toLocaleString()
            : 'Never'}
        </Text>
      </View>
      <View className="flex-row items-center justify-between py-2">
        <Text className="text-sm text-gray-600 dark:text-gray-400">Next scheduled sync</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400">
          {googleSheetsQ.data?.nextScheduledAt
            ? new Date(googleSheetsQ.data.nextScheduledAt).toLocaleString()
            : 'Not scheduled'}
        </Text>
      </View>
      {googleSheetsMessage ? (
        <View className="mt-3 rounded-xl bg-green-50 px-4 py-3 dark:bg-green-950">
          <Text className="text-sm text-green-700 dark:text-green-100">{googleSheetsMessage}</Text>
        </View>
      ) : null}
      {googleSheetsError || googleSheetsQ.data?.lastError ? (
        <View className="mt-3 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-950">
          <Text className="text-sm text-red-700 dark:text-red-100">
            {googleSheetsError ?? googleSheetsQ.data?.lastError}
          </Text>
        </View>
      ) : null}
      <View className="mt-3 gap-2">
        <Button
          size="sm"
          onPress={handleConnectGoogleSheets}
          loading={googleSheetsStartMutation.isPending}
          disabled={isDemo}
        >
          {googleSheetsQ.data?.connected ? 'Reconnect Google Sheets' : 'Connect Google Sheets'}
        </Button>
        <View className="flex-row gap-2">
          <Button
            size="sm"
            className="flex-1"
            onPress={handleSyncGoogleSheetsNow}
            loading={googleSheetsSyncMutation.isPending}
            disabled={isDemo || !googleSheetsQ.data?.connected}
          >
            Sync Now
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            onPress={handleOpenGoogleSheet}
            disabled={!googleSheetsQ.data?.spreadsheetUrl}
          >
            Open Spreadsheet
          </Button>
        </View>
        <Button
          size="sm"
          variant="secondary"
          onPress={handleDisconnectGoogleSheets}
          loading={googleSheetsDisconnectMutation.isPending}
          disabled={isDemo || !googleSheetsQ.data?.connected}
        >
          Disconnect
        </Button>
      </View>
    </Card>
  );
}
